// =============================================================================
// ULTRA LOOT ITEMS — Final Boss Chest
// =============================================================================
// 7 legendary items that drop exclusively from the Ultra Boss Chest.
// 2 cards shown at a time. Each item can only be obtained ONCE per run.

import type { GameState } from './types';
// BETA 20.72 — shape-aware bursts for ultra item VFX (these mechanics fired
// invisibly; every proc below now has its own owned look)
import { spawnParticles } from './types';

// ---------------------------------------------------------------------------
// Interface
// ---------------------------------------------------------------------------
export interface UltraItem {
  id: string;
  name: string;
  description: string;
  flavorText: string;
  /** Absolute path from /public (e.g. '/props/ultra_items/ultra_item_xxx.png') — or emoji */
  sprite: string;
  spriteIsEmoji?: boolean;
  rarity: 'legendary';
  onPickup: (state: GameState) => void;
  onUpdate?: (state: GameState, dt: number) => void;
  /** BETA 20.48 — optional teardown when an ultra is deactivated mid-run
   *  (Weapon Lab toggle-off). Undo any lingering effect: The Grind's permanent
   *  damage, Gained a Follower's allies, etc. Items with no residue omit it. */
  onRemove?: (state: GameState) => void;
}

// ---------------------------------------------------------------------------
// Item Definitions
// ---------------------------------------------------------------------------
export const ULTRA_ITEMS: UltraItem[] = [

  // -------------------------------------------------------------------------
  // 1. 1000 LB MOMENTUM
  // -------------------------------------------------------------------------
  {
    id: 'ultra_1000lb_momentum',
    name: '1000 LB Momentum',
    description: 'Kill 5 enemies in 3s → MOMENTUM: a 1-second surge of 3× damage that pierces every enemy.',
    flavorText: '"Nothing stops a thousand pounds in motion."',
    sprite: '/props/ultra_items/ultra_item_1000lb_momentum.png',
    rarity: 'legendary',
    onPickup: (state) => {
      state.player.ultraState = state.player.ultraState ?? {};
      const u = state.player.ultraState;
      u.momentum_killCount = 0;
      u.momentum_killTimer = 0;
      u.momentum_charged = false;
      // BETA 20.41 — full implementation (was a dead stub). Seed the kill-delta
      // tracker with the CURRENT kill count so pre-pickup kills don't insta-charge.
      u.momentum_prevKills = state.killCount ?? 0;
      u.momentum_buffTimer = 0;
      u.momentum_buffActive = false;
      u.momentum_procs = 0;
    },
    // BETA 20.41 — was a no-op stub (only ticked a timer nothing set). Now fully
    // wired: count kills in a rolling 3s window → at 5, CHARGE → next ~1s of
    // attacks deal 3× damage and pierce all. Self-contained in ultraState; reuses
    // the proven Donation/Grind damageMultiplier add/revert + N08 pierce field.
    onUpdate: (state, dt) => {
      const u = state.player.ultraState;
      if (!u) return;
      const p = state.player;

      // 1) Tick the rolling 3s kill window; reset the count when it lapses.
      if (u.momentum_killTimer > 0) {
        u.momentum_killTimer -= dt;
        if (u.momentum_killTimer <= 0) u.momentum_killCount = 0;
      }

      // 2) Detect new kills (delta). Don't accumulate while already charged or
      //    mid-buff (so a burst can't re-charge during the active window).
      const kills = state.killCount ?? 0;
      const newKills = Math.max(0, kills - (u.momentum_prevKills ?? kills));
      u.momentum_prevKills = kills;
      if (newKills > 0 && !u.momentum_charged && (u.momentum_buffTimer ?? 0) <= 0) {
        u.momentum_killCount = (u.momentum_killCount ?? 0) + newKills;
        u.momentum_killTimer = 3.0;   // refresh the rolling 3s window on each kill
        // BETA 20.72 — momentum building: white speed-lines kick BACK along the
        // player's movement heading on each in-window kill (a freight train
        // shedding air). Random direction fallback when standing still.
        const mdir = (p.vx || p.vy)
          ? Math.atan2(-(p.vy ?? 0), -(p.vx ?? 0))
          : Math.random() * Math.PI * 2;
        spawnParticles(state.particles, p.x, p.y, '#ffffff', 3, {
          shape: 'spark', dir: mdir, spread: 0.35, speedMin: 260, speedMax: 420,
          stretch: 7, lifeMin: 0.15, lifeMax: 0.28, fadePow: 1.5,
        });
        if (u.momentum_killCount >= 5) {
          u.momentum_killCount = 0;
          u.momentum_killTimer = 0;
          u.momentum_charged = true;
        }
      }

      // 3) Consume the charge → open a ~1s window of 3× damage + pierce-all.
      if (u.momentum_charged) {
        u.momentum_charged = false;
        u.momentum_buffActive = true;
        u.momentum_buffTimer = 1.0;                                     // catches the next attack(s) even on slow weapons
        p.damageMultiplier = (p.damageMultiplier ?? 1) + 2;            // +200% = 3× (persists — aggregator never resets damageMultiplier)
        // NOTE: pierce is NOT added here. applyAllBonusesToPlayer recomputes
        // projectilePierceBonus with `=` EVERY frame (itemsAdapter ~2159), so a
        // one-time +99 survived only a single frame. It's re-asserted per-frame
        // in step 5 below (this onUpdate runs AFTER the aggregator each tick).
        u.momentum_procs = (u.momentum_procs ?? 0) + 1;                // telemetry (was always 0)
        state.pickupNotifications?.push({ text: '⚡ MOMENTUM — 3× + PIERCE!', color: '#ffcc00', timer: 1.5 });
        // BETA 20.72 — full-charge flare + consume burst are ONE moment by
        // design: step 2 sets momentum_charged and this block consumes it the
        // SAME frame (sequential ifs, no charged-idle state). So the whole
        // payoff lands here, layered: white aura flare rings → 8 gold sparks
        // 360° → heavy white-hot wash + tumbling gold stars.
        spawnParticles(state.particles, p.x, p.y, '#ffffff', 2, {
          shape: 'ring', speedMin: 0, speedMax: 10, sizeMin: 18, sizeMax: 26,
          lifeMin: 0.3, lifeMax: 0.4,
        });
        spawnParticles(state.particles, p.x, p.y, '#ffd700', 8, {
          shape: 'spark', speedMin: 200, speedMax: 340, stretch: 5,
          lifeMin: 0.25, lifeMax: 0.4,
        });
        spawnParticles(state.particles, p.x, p.y, '#fff7cc', 10, {
          shape: 'spark', speedMin: 320, speedMax: 520, stretch: 5,
          lifeMin: 0.2, lifeMax: 0.35, fadePow: 1.5,
        });
        spawnParticles(state.particles, p.x, p.y, '#ffd700', 4, {
          shape: 'star', speedMin: 80, speedMax: 180, spin: 6, spinRandomSign: true,
          lifeMin: 0.4, lifeMax: 0.6, sizeMin: 3, sizeMax: 5,
        });
      }

      // 4) Revert the window — subtract our damageMultiplier contribution so
      //    concurrent buffs (Grind/Donation) and the N08 perc stay correct.
      //    (pierce needs no subtract — the per-frame aggregator wipe reverts it.)
      if ((u.momentum_buffTimer ?? 0) > 0) {
        u.momentum_buffTimer -= dt;
        if (u.momentum_buffTimer <= 0 && u.momentum_buffActive) {
          p.damageMultiplier = (p.damageMultiplier ?? 1) - 2;
          u.momentum_buffActive = false;
        }
      }

      // 5) BETA 21.42 — re-assert pierce-all EVERY active frame. itemsModule's
      //    aggregator (runs BEFORE this onUpdate) just reset projectilePierceBonus
      //    to the perc/item total via `=`, so adding +99 here — after the wipe,
      //    before the next frame's weapon fire — is what actually delivers the
      //    full ~1s of pierce. Previously the buff lasted a single frame. No
      //    revert needed: the next-frame wipe removes it the moment we stop.
      if (u.momentum_buffActive) {
        p.projectilePierceBonus = (p.projectilePierceBonus ?? 0) + 99;
      }
    },
  },

  // -------------------------------------------------------------------------
  // 2. TACOS OF THE BELL
  // -------------------------------------------------------------------------
  {
    id: 'ultra_tacos_of_the_bell',
    name: 'Tacos of the Bell',
    description: 'Movement leaves a fire trail. Enemies walking through it take 8 dmg/sec.',
    flavorText: '"The aftermath is everyone else\'s problem."',
    sprite: '/props/ultra_items/ultra_item_tacos_of_the_bell.png',
    rarity: 'legendary',
    onPickup: (state) => {
      state.player.ultraState = state.player.ultraState ?? {};
      state.player.ultraState.taco_fireTrails = [];
      state.player.ultraState.taco_spawnTimer = 0;
    },
    onUpdate: (state, dt) => {
      const u = state.player.ultraState;
      if (!u || !u.taco_fireTrails) return;

      const speed = Math.hypot(state.player.vx ?? 0, state.player.vy ?? 0);
      const trailRadius = 12 + Math.min(speed * 0.3, 20);

      u.taco_spawnTimer = (u.taco_spawnTimer ?? 0) + dt;
      if (u.taco_spawnTimer >= 0.08) {
        u.taco_spawnTimer = 0;
        u.taco_fireTrails.push({ x: state.player.x, y: state.player.y, radius: trailRadius, lifetime: 2.0 });
      }

      u.taco_fireTrails = u.taco_fireTrails.filter((t: any) => {
        t.lifetime -= dt;
        for (const enemy of (state.enemies ?? [])) {
          if (Math.hypot(enemy.x - t.x, enemy.y - t.y) < t.radius + 16) {
            (state.pendingEnemyDamage ??= []).push({ id: enemy.id, amount: 8 * dt });
            u.taco_damageTotal = (u.taco_damageTotal ?? 0) + 8 * dt; // BETA 20.39 — telemetry
          }
        }
        return t.lifetime > 0;
      });
    },
  },

  // -------------------------------------------------------------------------
  // 3. KEVIN'S LAST STAND
  // -------------------------------------------------------------------------
  {
    id: 'ultra_kevins_last_stand',
    name: "Kevin's Last Stand",
    description: 'Below 20% HP → Kevin orbits you for 8s, dealing 40 dmg on contact. 90s cooldown.',
    flavorText: '"He was always watching. Waiting."',
    sprite: '/props/ultra_items/ultra_item_kevins_last_stand.png',
    rarity: 'legendary',
    onPickup: (state) => {
      state.player.ultraState = state.player.ultraState ?? {};
      state.player.ultraState.kevin_cooldown = 0;
      state.player.ultraState.kevin_active = false;
      state.player.ultraState.kevin_timer = 0;
      state.player.ultraState.kevin_angle = 0;
    },
    onUpdate: (state, dt) => {
      const u = state.player.ultraState;
      if (!u) return;
      if (u.kevin_cooldown > 0) u.kevin_cooldown -= dt;

      const hpPct = state.player.hp / state.player.maxHp;
      if (!u.kevin_active && hpPct < 0.2 && u.kevin_cooldown <= 0) {
        u.kevin_active = true;
        u.kevin_timer = 8.0;
        u.kevin_angle = 0;
        u.kevin_cooldown = 90;
        u.kevin_activations = (u.kevin_activations ?? 0) + 1; // BETA 20.39 — telemetry
      }

      if (u.kevin_active) {
        u.kevin_timer -= dt;
        u.kevin_angle += dt * 4;
        const kevinX = state.player.x + Math.cos(u.kevin_angle) * 80;
        const kevinY = state.player.y + Math.sin(u.kevin_angle) * 80;
        u.kevin_x = kevinX;
        u.kevin_y = kevinY;

        // BETA 20.72 — Kevin's wake: drop a fading cyan afterimage orb every
        // ~0.18s along the orbit — short life leaves a dotted trailing halo.
        u.kevin_afterTimer = (u.kevin_afterTimer ?? 0) + dt;
        if (u.kevin_afterTimer >= 0.18) {
          u.kevin_afterTimer = 0;
          spawnParticles(state.particles, kevinX, kevinY, '#00e5ff', 1, {
            shape: 'mote', speedMin: 0, speedMax: 8, lifeMin: 0.25, lifeMax: 0.4,
            sizeMin: 5, sizeMax: 7, fadePow: 1.5,
          });
        }
        // BETA 20.72 — Kevin connects: cyan halo pulse. THROTTLED — contact
        // damage fires EVERY FRAME per enemy in range; unthrottled rings would
        // spam 60/sec into the particle pool.
        u.kevin_ringCd = Math.max(0, (u.kevin_ringCd ?? 0) - dt);
        let kevinHit = false;

        for (const enemy of (state.enemies ?? [])) {
          if (Math.hypot(enemy.x - kevinX, enemy.y - kevinY) < 24) {
            const ang = Math.atan2(enemy.y - kevinY, enemy.x - kevinX);
            (state.pendingEnemyDamage ??= []).push({
              id: enemy.id, amount: 40,
              knockbackX: Math.cos(ang) * 20,
              knockbackY: Math.sin(ang) * 20,
            });
            kevinHit = true; // BETA 20.72 — flag for the throttled pulse below
          }
        }

        // BETA 20.72 — max ~7 pulses/sec even while grinding through a crowd
        if (kevinHit && u.kevin_ringCd <= 0) {
          u.kevin_ringCd = 0.15;
          spawnParticles(state.particles, kevinX, kevinY, '#00e5ff', 1, {
            shape: 'ring', speedMin: 0, speedMax: 0, sizeMin: 10, sizeMax: 14,
            lifeMin: 0.25, lifeMax: 0.35,
          });
          spawnParticles(state.particles, kevinX, kevinY, '#00e5ff', 2, {
            shape: 'spark', speedMin: 120, speedMax: 220, stretch: 4,
            lifeMin: 0.15, lifeMax: 0.25,
          });
        }

        if (u.kevin_timer <= 0) u.kevin_active = false;
      }
    },
  },

  // -------------------------------------------------------------------------
  // 4. DONATION ALERT
  // -------------------------------------------------------------------------
  {
    id: 'ultra_donation_alert',
    name: 'Donation Alert',
    description: 'On a kill streak (no recent damage), chat "donates" → a temporary +50–100% DAMAGE surge for 8s. Luckier = more donations.',
    flavorText: '"PogChamp just donated $5! They said: get better loot."',
    sprite: '/props/ultra_items/ultra_item_donation_alert.png',
    rarity: 'legendary',
    onPickup: (state) => {
      state.player.ultraState = state.player.ultraState ?? {};
      state.player.ultraState.donation_killCount = 0;
      state.player.ultraState.donation_alertTimer = 0;
    },
    onUpdate: (state, dt) => {
      const u = state.player.ultraState;
      if (!u) return;
      if (u.donation_alertTimer > 0) u.donation_alertTimer -= dt;
      // BETA 20.72 — MONEY SHOWER. The live proc fires in engine.ts's donation
      // surge block (off-limits to this module), so detect it here by edge on
      // the donation_procs telemetry counter — 1 frame of latency, invisible.
      const procs = u.donation_procs ?? 0;
      if (procs > (u.donation_vfxSeen ?? 0)) {
        u.donation_vfxSeen = procs;
        u.donation_showerTimer = 1.5;   // rain gold for 1.5s
        u.donation_showerTick = 0;
        // gold ring pops at the player the instant chat donates
        spawnParticles(state.particles, state.player.x, state.player.y, '#ffd700', 1, {
          shape: 'ring', speedMin: 0, speedMax: 0, sizeMin: 16, sizeMax: 20,
          lifeMin: 0.35, lifeMax: 0.45,
        });
      }
      // drip-feed the shower: 2 gold stars per 0.08s tick falling from a band
      // above the player (~28 stars total) — never one big burst allocation
      if ((u.donation_showerTimer ?? 0) > 0) {
        u.donation_showerTimer -= dt;
        u.donation_showerTick = (u.donation_showerTick ?? 0) + dt;
        if (u.donation_showerTick >= 0.08) {
          u.donation_showerTick = 0;
          spawnParticles(
            state.particles,
            state.player.x + (Math.random() * 180 - 90),
            state.player.y - 150,
            '#ffd700', 2, {
              shape: 'star', dir: Math.PI / 2, spread: 0.1, speedMin: 30, speedMax: 80,
              gravity: 260, spin: 5, spinRandomSign: true,
              lifeMin: 0.7, lifeMax: 1.0, sizeMin: 3, sizeMax: 5,
            },
          );
        }
      }
    },
  },

  // -------------------------------------------------------------------------
  // 5. RAGE QUIT AURA
  // -------------------------------------------------------------------------
  {
    id: 'ultra_rage_quit_aura',
    name: 'Rage Quit Aura',
    description: 'Once per run: on death, WIPE THE MAP (20,000 dmg to all), respawn at 30% HP with 3s of blinking invulnerability, and all XP + gold zooms to you.',
    flavorText: '"The controller didn\'t survive. You did."',
    sprite: '/props/ultra_items/ultra_item_rage_quit_aura.png',
    rarity: 'legendary',
    onPickup: (state) => {
      state.player.ultraState = state.player.ultraState ?? {};
      state.player.ultraState.rageQuit_available = true;
    },
    // BETA 20.72 — map-wipe shockwave. The wipe itself fires in engine.ts's
    // death intercept (off-limits to this module); detect it by edge on the
    // rageQuit_used telemetry counter — 1 frame late, invisible under the
    // 1.0s/40-intensity screenshake. Respawn is IN PLACE, so player-position
    // spawns land exactly where the wipe happened.
    onUpdate: (state, dt) => {
      const u = state.player.ultraState;
      if (!u) return;
      const used = u.rageQuit_used ?? 0;
      if (used > (u.rageQuit_vfxSeen ?? 0)) {
        u.rageQuit_vfxSeen = used;
        u.rageQuit_vfxTimer = 0.35;     // staggered shockwave-ring window
        u.rageQuit_vfxRing = 0;
        u.rageQuit_vfxRingTick = 0;
        const p = state.player;
        // It kills EVERYTHING on screen — sell it: 50 tumbling debris shards
        // (white-hot + rage red) blasted outward and sinking...
        spawnParticles(state.particles, p.x, p.y, '#ffffff', 30, {
          shape: 'shard', speedMin: 180, speedMax: 520, spin: 10, spinRandomSign: true,
          gravity: 60, lifeMin: 0.5, lifeMax: 0.9, sizeMin: 3, sizeMax: 6,
        });
        spawnParticles(state.particles, p.x, p.y, '#ff2200', 20, {
          shape: 'shard', speedMin: 180, speedMax: 520, spin: 10, spinRandomSign: true,
          gravity: 60, lifeMin: 0.5, lifeMax: 0.9, sizeMin: 3, sizeMax: 6,
        });
        // ...over a fast red spark wash radiating from the player
        spawnParticles(state.particles, p.x, p.y, '#ff2200', 12, {
          shape: 'spark', speedMin: 300, speedMax: 600, stretch: 6,
          lifeMin: 0.25, lifeMax: 0.4, fadePow: 1.5,
        });
      }
      // 4 GENUINELY staggered white shockwave rings (one per 0.1s, first
      // immediate), each spawning bigger than the last → full-screen ripple
      if ((u.rageQuit_vfxTimer ?? 0) > 0) {
        u.rageQuit_vfxTimer -= dt;
        u.rageQuit_vfxRingTick = (u.rageQuit_vfxRingTick ?? 0) - dt;
        if (u.rageQuit_vfxRingTick <= 0) {
          u.rageQuit_vfxRingTick = 0.1;
          const n = u.rageQuit_vfxRing ?? 0;
          spawnParticles(state.particles, state.player.x, state.player.y, '#ffffff', 1, {
            shape: 'ring', speedMin: 0, speedMax: 0,
            sizeMin: 24 + n * 18, sizeMax: 32 + n * 18,
            lifeMin: 0.4, lifeMax: 0.5,
          });
          u.rageQuit_vfxRing = n + 1;
        }
      }
    },
  },

  // -------------------------------------------------------------------------
  // 6. THE GRIND (sprite missing from pack — uses emoji)
  // -------------------------------------------------------------------------
  {
    id: 'ultra_the_grind',
    name: 'The Grind',
    description: 'Every 60s survived → +3% permanent damage. No cap.',
    flavorText: '"The longer you stay, the more dangerous you become."',
    sprite: '⚙',
    spriteIsEmoji: true,
    rarity: 'legendary',
    onPickup: (state) => {
      state.player.ultraState = state.player.ultraState ?? {};
      state.player.ultraState.grind_timer = 0;
      state.player.ultraState.grind_stacks = 0;
    },
    onUpdate: (state, dt) => {
      const u = state.player.ultraState;
      if (!u) return;
      u.grind_timer += dt;
      if (u.grind_timer >= 60) {
        u.grind_timer -= 60;
        u.grind_stacks = (u.grind_stacks ?? 0) + 1;
        state.player.damageMultiplier = (state.player.damageMultiplier ?? 1) + 0.03;
        state.pickupNotifications.push({ text: '+3% DMG (The Grind)', color: '#ff8800', timer: 2.5 });
        // BETA 20.72 — the grind never stops: deep-ember motes drift up off
        // the player on each 60s stack (#cc5500 base — deliberately darker
        // than Silly Juice's #ff9933 so the two orange identities never read
        // alike), capped with two upward heat-streak sparks.
        spawnParticles(state.particles, state.player.x, state.player.y, '#cc5500', 4, {
          shape: 'mote', speedMin: 10, speedMax: 35, gravity: -70,
          lifeMin: 0.7, lifeMax: 1.1, sizeMin: 2, sizeMax: 4,
        });
        spawnParticles(state.particles, state.player.x, state.player.y, '#ff6600', 2, {
          shape: 'spark', dir: -Math.PI / 2, spread: 0.5, speedMin: 60, speedMax: 120,
          stretch: 3, lifeMin: 0.4, lifeMax: 0.6,
        });
      }
    },
    onRemove: (state) => {
      const u = state.player.ultraState;
      if (!u) return;
      // Undo the permanent damage this item granted, then clear its counters.
      const stacks = u.grind_stacks ?? 0;
      state.player.damageMultiplier = (state.player.damageMultiplier ?? 1) - stacks * 0.03;
      u.grind_stacks = 0;
      u.grind_timer = 0;
    },
  },

  // -------------------------------------------------------------------------
  // 7. GAINED A FOLLOWER
  // -------------------------------------------------------------------------
  {
    id: 'ultra_gained_a_follower',
    name: 'Gained a Follower',
    description: 'Enemies you kill have a 15% chance to rise as a friendly follower — 2× speed, hits hard, NEVER dies, and fights at your side. Up to 8 at once.',
    flavorText: '"They just needed to feel something real."',
    sprite: '/props/ultra_items/ultra_item_gained_a_follower.png',
    rarity: 'legendary',
    onPickup: (state) => {
      state.player.ultraState = state.player.ultraState ?? {};
      const u = state.player.ultraState;
      // Stackable-ready: re-pick keeps the existing crew + bumps convert chance.
      u.follower_list = u.follower_list ?? [];
      u.follower_lastKills = state.killCount ?? 0;
      u.follower_chance = (u.follower_chance ?? 0) + 0.15;
    },
    // BETA 20.28 — full implementation (was a stub). Self-contained in ultraState
    // (no enemy-loop changes): on kill → roll convert → spawn an immortal ally
    // that homes onto enemies and attacks via pendingEnemyDamage; idles near you.
    onUpdate: (state, dt) => {
      const u = state.player.ultraState;
      if (!u || !u.follower_list) return;
      const MAX_FOLLOWERS = 8;
      const list = u.follower_list as Array<{ x: number; y: number; cooldown: number; bob: number }>;

      // 1) Convert-on-kill — detect new kills via killCount delta, roll chance.
      const kills = state.killCount ?? 0;
      let killDelta = Math.max(0, kills - (u.follower_lastKills ?? kills));
      u.follower_lastKills = kills;
      const chance = u.follower_chance ?? 0.15;
      let spawnBudget = 2; // cap spawns per frame so a kill-burst can't flood
      while (killDelta > 0 && spawnBudget > 0 && list.length < MAX_FOLLOWERS) {
        killDelta--;
        if (Math.random() < chance) {
          const ang = Math.random() * Math.PI * 2;
          list.push({
            x: state.player.x + Math.cos(ang) * 46,
            y: state.player.y + Math.sin(ang) * 46,
            cooldown: 0,
            bob: Math.random() * Math.PI * 2,
          });
          spawnBudget--;
          u.follower_spawnedTotal = (u.follower_spawnedTotal ?? 0) + 1; // BETA 20.39 — telemetry
          state.pickupNotifications?.push({ text: '＋ Follower joined!', color: '#39ff14', timer: 1.6 });
        }
      }

      // 2) Each follower: home to the nearest enemy + melee it; else stay near you.
      const enemies = state.enemies ?? [];
      const SPEED = 320;       // ~2× a normal enemy's move speed
      const ATK_DMG = 28;      // per hit (~2× a normal enemy)
      const ATK_RANGE = 42;
      const ATK_CD = 0.45;
      const ENGAGE_RANGE = 640;
      for (const f of list) {
        f.cooldown = Math.max(0, (f.cooldown ?? 0) - dt);
        f.bob = (f.bob ?? 0) + dt * 6;
        let nearest: { id: number; x: number; y: number } | null = null;
        let nd = Infinity;
        for (const e of enemies) {
          if (!(e as { alive?: boolean }).alive) continue;
          const d = Math.hypot(e.x - f.x, e.y - f.y);
          if (d < nd) { nd = d; nearest = e as { id: number; x: number; y: number }; }
        }
        if (nearest && nd < ENGAGE_RANGE) {
          const dx = nearest.x - f.x, dy = nearest.y - f.y;
          const dist = Math.hypot(dx, dy) || 1;
          if (dist > ATK_RANGE) {
            f.x += (dx / dist) * SPEED * dt;
            f.y += (dy / dist) * SPEED * dt;
          } else if (f.cooldown <= 0) {
            (state.pendingEnemyDamage ??= []).push({
              id: nearest.id, amount: ATK_DMG,
              knockbackX: (dx / dist) * 240, knockbackY: (dy / dist) * 240,
            });
            f.cooldown = ATK_CD;
          }
        } else {
          const dx = state.player.x - f.x, dy = state.player.y - f.y;
          const dist = Math.hypot(dx, dy) || 1;
          if (dist > 70) {
            f.x += (dx / dist) * SPEED * dt;
            f.y += (dy / dist) * SPEED * dt;
          }
        }
      }
    },
    onRemove: (state) => {
      const u = state.player.ultraState;
      if (!u) return;
      // Disband the crew + reset convert-chance so a re-toggle starts fresh.
      u.follower_list = [];
      u.follower_chance = 0;
    },
  },
];

// ---------------------------------------------------------------------------
// Donation messages pool
// ---------------------------------------------------------------------------
export const DONATION_MESSAGES = [
  "PogChamp donated $5! They said: you're cooked",
  "xXPeanutLord69Xx donated $5! They said: skill issue",
  "Anonymous donated $5! They said: run it back",
  "BigBossFan2024 donated $5! They said: he's coming back",
  "ViewbotterActual donated $5! They said: this isn't real",
];

// ---------------------------------------------------------------------------
// Ultra Chest Pool Manager
// ---------------------------------------------------------------------------
export class UltraChestPool {
  private collectedIds: Set<string> = new Set();

  getAvailable(): UltraItem[] {
    return ULTRA_ITEMS.filter(item => !this.collectedIds.has(item.id));
  }

  rollChest(): UltraItem[] {
    const available = this.getAvailable();
    if (available.length === 0) return [];
    const shuffled = [...available].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, Math.min(2, shuffled.length));
  }

  collect(itemId: string, state: GameState): void {
    const item = ULTRA_ITEMS.find(i => i.id === itemId);
    if (!item) return;
    this.collectedIds.add(itemId);
    item.onPickup(state);
    if (item.onUpdate) {
      state.player.activeUltraItems = state.player.activeUltraItems ?? [];
      if (!state.player.activeUltraItems.includes(item.id)) {
        state.player.activeUltraItems.push(item.id);
      }
    }
  }

  /** BETA 20.48 — has this ultra item been collected this run? The Weapon Lab
   *  reads this for toggle state. Tracks ALL collected ultras, including ones
   *  whose whole mechanic is a one-time onPickup flag with no onUpdate. */
  isCollected(itemId: string): boolean {
    return this.collectedIds.has(itemId);
  }

  /** BETA 20.48 — deactivate an ultra item mid-run (Weapon Lab toggle-off):
   *  stop its onUpdate ticking, re-allow re-rolling it, and let the item undo
   *  lingering effects via its optional onRemove hook. */
  remove(itemId: string, state: GameState): void {
    const item = ULTRA_ITEMS.find(i => i.id === itemId);
    this.collectedIds.delete(itemId);
    if (state.player.activeUltraItems) {
      state.player.activeUltraItems = state.player.activeUltraItems.filter(id => id !== itemId);
    }
    item?.onRemove?.(state);
  }

  updateAll(state: GameState, dt: number): void {
    for (const id of (state.player.activeUltraItems ?? [])) {
      const item = ULTRA_ITEMS.find(i => i.id === id);
      item?.onUpdate?.(state, dt);
    }
  }

  reset(): void {
    this.collectedIds.clear();
  }
}

export const ultraChestPool = new UltraChestPool();
